import { PopupApiDto } from '@b2x/storefront-api-js-client/src/dto';
import { useCallback, useEffect, useState } from 'react';

import { Accordion, AccordionItem } from '../Accordion';
import { useGetPopupById } from '../api/usePopupsApi';
import { Button } from '../Button';
import { Container } from '../Container';
import { PopupContentTypeV2 } from '../contentTypes';
import { Div, H1, Li, P, Ul } from '../HTMLElement';
import { t } from '../i18n/i18n';
import { Page } from '../Page';
import { Route, Routes, useNavigate, useParams, useSearchParams } from '../router/router';
import { usePopups } from '../usePopups';

const PopupIdPage = () => {
  const { id } = useParams();
  const { getPopupById } = useGetPopupById();
  const { showPopup } = usePopups();
  const [searchParams] = useSearchParams();
  const onlyPreview = searchParams.get('onlyPreview') === 'true';
  const [popupConfiguration, setPopupConfiguration] = useState<PopupApiDto<PopupContentTypeV2>>();
  const [isError, setIsError] = useState(false);

  const openPopup = useCallback(() => {
    if (!id) return;

    getPopupById(id, { populate: { content: true, offer: true } }, 0)
      .then((popup) => {
        setPopupConfiguration(popup.data);
        if (onlyPreview) {
          popup.data.startMinimized = false;
          popup.data.delay = 0;
          popup.data.showOnExit = false;
        }
        showPopup(popup.data);
      })
      .catch(() => setIsError(true));
  }, [id, getPopupById, onlyPreview, showPopup]);

  useEffect(() => openPopup(), [openPopup]);

  return (
    <Page thingsToLoadBeforePageReady={[]}>
      <Container>
        <Div display="flex" flexDirection="column" gap={5} marginY={5}>
          <Div
            alignItems="center"
            display="flex"
            flexDirection="column"
            gap={3}
            justifyContent="center"
            textAlign="center"
          >
            <H1 margin={0}>{t('testPopupPage.idPage.title', { id })}</H1>
            <div>
              <P margin={0}>{t('testPopupPage.idPage.description')}</P>
              <P fontWeight={100} margin={0}>
                {t('testPopupPage.idPage.info')}
              </P>
            </div>
            <Button onClick={openPopup}>{t('testPopupPage.idPage.button')}</Button>
            {onlyPreview && (
              <P fontWeight={100} margin={0}>
                {t('testPopupPage.idPage.previewMode')}
              </P>
            )}
          </Div>
          {isError ? (
            <P className="text-danger" textAlign="center">
              {t('testPopupPage.idPage.error')}
            </P>
          ) : (
            <Accordion id="popup-configuration">
              <AccordionItem id="popup-configuration-item" title={t('testPopupPage.idPage.details')}>
                <Ul>
                  {Object.entries(popupConfiguration ?? {})
                    .filter(([_, value]) => typeof value !== 'object')
                    .map(([key, value]) => (
                      <Li display="flex" gap={2} key={key}>
                        <strong>{key}:</strong>
                        <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
                      </Li>
                    ))}
                </Ul>
              </AccordionItem>
            </Accordion>
          )}
        </Div>
      </Container>
    </Page>
  );
};

const PopupHomePage = () => {
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const id = formData.get('id') as string;
    navigate(`/test-popup/${id}?onlyPreview=${formData.get('preview') === 'on'}`);
  };

  return (
    <Page thingsToLoadBeforePageReady={[]}>
      <Container>
        <Div display="flex" flexDirection="column" gap={4} marginY={5} textAlign="center">
          <Div display="flex" flexDirection="column" gap={2}>
            <h1>{t('testPopupPage.homePage.title')}</h1>
            <p>{t('testPopupPage.homePage.description')}</p>
          </Div>
          <form onSubmit={handleSubmit}>
            <Div alignItems="center" display="flex" flexDirection="column" gap={3} justifyContent="center">
              <Div alignItems="center" display="flex" gap={2}>
                <label htmlFor="id">{t('testPopupPage.homePage.idPopup')}:</label>
                <input id="id" name="id" placeholder={t('testPopupPage.homePage.idPopup')} required type="text" />
              </Div>
              <Div alignItems="center" display="flex" gap={2}>
                <input id="preview" name="preview" type="checkbox" />
                <label htmlFor="preview">{t('testPopupPage.homePage.previewMode')}</label>
              </Div>
              <Button type="submit">{t('testPopupPage.homePage.submit')}</Button>
            </Div>
          </form>
        </Div>
      </Container>
    </Page>
  );
};

export const TestPopupPage = () => {
  return (
    <Routes>
      <Route element={<PopupIdPage />} path=":id" />
      <Route element={<PopupHomePage />} index />
    </Routes>
  );
};
